
// File: components/audio/utils.ts
import * as Tone from 'tone';

/**
 * Safely schedules a parameter ramp while avoiding common Web Audio scheduling errors.
 * Ensures that the start time is in the future and strictly greater than previous events
 * by anchoring with a setValueAtTime before starting the ramp.
 */
export const safeRamp = (param: Tone.Param<any> | Tone.Signal<any>, value: number, duration: number, time?: number) => {
    if (!param) return;
    
    const now = Tone.now();
    // Safety margin to ensure scheduling happens in the future
    const margin = 0.1;
    const startTime = time !== undefined ? Math.max(time, now + margin) : (now + margin);

    try {
        // 1. Clear any pending automations from 'now' to avoid conflicts
        param.cancelScheduledValues(now);
        
        // 2. Capture the current value to use as a starting anchor.
        // This is the most critical step to prevent "strictly greater" errors 
        // when a ramp is scheduled immediately after cancellation.
        const currentVal = (param as any).value !== undefined ? (param as any).value : 0;
        param.setValueAtTime(currentVal, startTime);
        
        // 3. Schedule the ramp to start a tiny fraction of a second after the anchor.
        // Tone's rampTo handles the transition logic smoothly.
        const target = value <= -60 ? -100 : value; 
        param.rampTo(target, duration, startTime + 0.005);
    } catch (e) {
        // Silently log scheduling warnings to prevent game crashes during rapid state changes
        console.warn("Audio scheduling warning (safeRamp):", e);
    }
};
